import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { setWorkspaceLoadingState } from 'api/graphQL/reactiveVars/workspaceLoadingState'

import Workspace2EditorActionBar from 'components/Workspace2EditorActionBar/Workspace2EditorActionBar'
import { EditorActionBarStoreProvider } from 'components/Workspace2EditorActionBar/store/EditorActionBarStoreProvider'
import React, { memo, useEffect, useRef } from 'react'
import { useShallowSelector } from './Workspace2Store'
import { useBestPracticesQuery } from './api/useBestPracticesQuery'
import { useProjectDataQuery } from './api/useProjectDataQuery'
import {
    EditorTabs,
    ProductsSharedPropertyLabel,
    ReviewAssembly,
    TabItem,
    UnderMaintenance,
} from './components'
import { useInitialCardPositions } from './components/TrackChangesPanel/hooks/useInitialCardPositions'
import { TreeView } from './components/TreeView/TreeView'

import { KeyModificationWarningDialog } from './components/KeyModificationWarningDialog'
import { SustainabilityWarningDialog } from './components/SustainabilityWarningDialog'
import { ClashDetectionDialog } from './features/clashDetection/components/ClashDetectionDialog'
import HtmlParagraphPasteProgressDialog from './features/copypaste/components/HtmlParagraphPasteProgressDialog'

import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useShallowHoveredItems } from './HoveredItemStore'
import { useCopyPasteShallowSelector } from './features/copypaste/store/CopyPasteStore'
import { useServiceWorkerListener } from './features/hyperlinkSelection'
import {
    useContentItemFetcher,
    useEditorActions,
    useFamilyAddedProductTypes,
    useLayoutEffects,
    usePermissionChecks,
    useTreeDataMapping,
} from './hooks'
import { useBuildNodeMapOnTreeDataChange } from './hooks/useBuildNodeMapOnTreeDataChange'
import { useWorkspaceEditorStyles } from './styles'
import { ScrollableTreeRef, WorkspaceEditorProps } from './types/types'

const editorTabs: TabItem[] = [
    { label: 'Part 1 - General', disabled: false },
    { label: 'Part 2 - Products', disabled: false },
    { label: 'Part 3 - Execution', disabled: false },
]

export const WorkspaceEditor = memo<WorkspaceEditorProps>(
    ({
        selectedProjectElementNode,
        selectedElementData,
        projectId,
        isElementTreeLoading,
        insertNewProductTypeElement,
    }) => {
        const classes = useWorkspaceEditorStyles()
        const treeRef = useRef<ScrollableTreeRef>({} as ScrollableTreeRef)
        const { previewFeatures } = usePreviewFeatures()

        const {
            isTreeLoading,
            treeData,
            activeTab,
            setDefault,
            setTreeData,
            setTreeRef,
            setProjectId,
        } = useShallowSelector((s) => ({
            isTreeLoading: s.isTreeLoading,
            treeData: s.treeData,
            activeTab: s.activeTab,
            setDefault: s.actions.setDefault,
            setTreeData: s.actions.setTreeData,
            setTreeRef: s.actions.setTreeRef,
            setProjectId: s.actions.setProjectId,
        }))

        const {
            showPasteDialog,
            contentInsertVariables,
        } = useCopyPasteShallowSelector((state) => ({
            showPasteDialog: state.showPasteDialog,
            contentInsertVariables: state.contentInsertVariables,
        }))

        const { showClashDetectionDialog, showSustainabilityWarningDialog } = useShallowHoveredItems(
            (state) => ({
                showClashDetectionDialog: state.showClashDetectionDialog,
                showSustainabilityWarningDialog: state.showSustainabilityWarningDialog,
            })
        )

        // hook that maps content items to tree data
        const {
            mapTreeData,
            memoContentItemViewNodes,
            withKeysToFetch,
            withKeysToCollapse,
            memoContentItemViewIds,
            memoArticleContentItemIds,
        } = useTreeDataMapping()

        const permission = usePermissionChecks(
            treeData,
            selectedProjectElementNode
        )

        /**
         * hook that does following:
         * - creates memoized fetcher function.
         * - creates memoized editor tab object.
         * - side-effects that fetches product family if necessary.
         */
        const {
            fetchContentItemView: fetchProductFamily,
        } = useContentItemFetcher({
            projectId,
            treeData,
            setTreeData,
            permission,
        })

        const { fetchBestPracticeElementData } = useBestPracticesQuery({
            projectId,
        })
        const { fetchProjectData } = useProjectDataQuery({ projectId })

        // hook that manages actions in the Workspace Editor
        const { handleEditorTabChange } = useEditorActions({
            treeData,
            fetchProductFamily,
            withKeysToFetch,
            withKeysToCollapse,
            memoContentItemViewIds,
            memoArticleContentItemIds,
            showSharedPropertyLabel: permission.showSharedPropertyLabel,
        })

        const isLoading = isElementTreeLoading || isTreeLoading

        // Side-effect that resets the workspace store when component unmounted
        useLayoutEffects(setDefault)

        // Set projectId in store when it changes
        useEffect(() => {
            if (projectId) {
                setProjectId(projectId)
            }
        }, [projectId, setProjectId])

        useEffect(() => {
            if (
                selectedProjectElementNode?.elementId &&
                !isLoading &&
                permission?.shouldAllowChange
            ) {
                setWorkspaceLoadingState(false)
            }
        }, [
            selectedProjectElementNode?.elementId,
            isLoading,
            permission?.shouldAllowChange,
        ])
        // side effect to detect content changes and map tree data
        useEffect(() => mapTreeData(memoContentItemViewNodes), [
            memoContentItemViewNodes,
        ])

        // side effect to set initial card positions
        useInitialCardPositions(treeData)

        useFamilyAddedProductTypes({
            selectedProjectElementNode,
            projectId,
        })
        // side effect to build contentItem.id to node map when tree data changes
        useBuildNodeMapOnTreeDataChange(treeData, treeRef, setTreeRef)

        // Listen for service worker messages to update hyperlink selection states
        useServiceWorkerListener()

        if (permission.isUnderMaintenance) return <UnderMaintenance />

        if (permission.isAssemblyTypeProject) {
            return (
                <EditorActionBarStoreProvider projectId={projectId ?? ''}>
                    <ReviewAssembly
                        selectedProjectElementNode={selectedProjectElementNode}
                        uniformatClassificationProperties={
                            selectedElementData?.uniformatClassificationProperties
                        }
                    />
                </EditorActionBarStoreProvider>
            )
        }

        return (
            <>
                <EditorActionBarStoreProvider projectId={projectId ?? ''}>
                    <EditorTabs
                        tabs={editorTabs}
                        value={activeTab}
                        variant={'standard'}
                        onChange={handleEditorTabChange}
                        aria-label="Editor tabs"
                    />
                    <div
                        className={classes.workspaceEditorContainer}
                        role="region"
                        aria-label="Workspace Editor"
                        id="workspaceEditorContainer"
                    >
                        {permission.showSharedPropertyLabel ? (
                            <ProductsSharedPropertyLabel content="Please select the Family Level to load Part 1 and Part 3 content" />
                        ) : (
                            <>
                                <Workspace2EditorActionBar
                                    isLoading={isLoading}
                                    insertNewProductTypeElement={
                                        insertNewProductTypeElement
                                    }
                                />
                                <TreeView
                                    treeData={treeData}
                                    fetchProductFamily={fetchProductFamily}
                                    fetchBestPracticeElementData={
                                        fetchBestPracticeElementData
                                    }
                                    fetchProjectData={fetchProjectData}
                                    isLoading={isLoading}
                                    treeRef={treeRef}
                                    projectId={projectId}
                                    selectedProjectElementNode={
                                        selectedProjectElementNode as ProjectElementNode
                                    }
                                />
                            </>
                        )}
                    </div>
                </EditorActionBarStoreProvider>
                {showPasteDialog && contentInsertVariables && (
                    <HtmlParagraphPasteProgressDialog
                        contentInsertVariables={contentInsertVariables}
                    />
                )}
                {showClashDetectionDialog && <ClashDetectionDialog />}
                {showSustainabilityWarningDialog && <SustainabilityWarningDialog />}
                <KeyModificationWarningDialog />
            </>
        )
    }
)
